import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import NotificationCenter from './NotificationCenter';
import CreditBadge from './CreditBadge';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  const navLinks = [
    { href: '/#features', label: t('header.features') },
    { href: '/#pricing', label: t('header.pricing') },
    { href: '/guide', label: t('header.guides') },
    { href: '/#headless', label: t('headless.navLabel') },
    { href: '/#faq', label: 'FAQ' },
    { href: '/about', label: t('header.about') },
    { href: '/contact', label: 'Contact' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass" data-testid="header">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <Logo size={40} />
            <span className="font-['Outfit'] font-semibold text-xl tracking-tight">
              AEO Orchestra <span className="text-[#0055FF]">AI</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => 
              link.href.startsWith('/') && !link.href.startsWith('/#') && !link.href.startsWith('mailto:') ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-[#A1A1AA] hover:text-white transition-colors text-sm font-medium"
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={link.href.startsWith('/#') ? (e) => {
                    e.preventDefault();
                    const id = link.href.replace('/#', '');
                    const el = document.getElementById(id);
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                    else window.location.href = link.href;
                  } : undefined}
                  className="text-[#A1A1AA] hover:text-white transition-colors text-sm font-medium"
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  {...(link.href.startsWith('mailto:') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  {link.label}
                </a>
              )
            )}
          </nav>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            {user ? (
              <>
                {user.role === 'admin' && (
                  <Link to="/admin">
                    <Button variant="ghost" className="text-[#A1A1AA] hover:text-white" data-testid="admin-link">
                      Dashboard
                    </Button>
                  </Link>
                )}
                <Link to="/dashboard">
                  <Button variant="ghost" className="text-[#A1A1AA] hover:text-white" data-testid="dashboard-link">
                    My Licenses
                  </Button>
                </Link>
                <CreditBadge />
                <NotificationCenter />
                <Button
                  onClick={logout}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5"
                  data-testid="logout-btn"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-[#A1A1AA] hover:text-white" data-testid="login-link">
                    {t('header.login')}
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button className="bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-full px-6" data-testid="get-started-btn">
                    {t('header.tryFree')}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-white/10 pt-4">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => 
                link.href.startsWith('/') ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-[#A1A1AA] hover:text-white transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    className="text-[#A1A1AA] hover:text-white transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                )
              )}
              <div className="flex flex-col gap-2 mt-4">
                {user ? (
                  <>
                    <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full border-white/20">My Licenses</Button>
                    </Link>
                    <Button onClick={logout} variant="ghost" className="w-full">Logout</Button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full border-white/20">Accedi</Button>
                    </Link>
                    <Link to="/pricing" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-[#0055FF]">Inizia Ora</Button>
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
